import { Button } from '@heroui/react'
import { Controller } from 'react-hook-form'
import type { Control, FieldArrayWithId, FieldErrors } from 'react-hook-form'

import { TextFieldControl } from '@/components/ui/form-fields'
import { FormSection, FormSectionEmpty } from '@/components/ui/form-section'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { ImageThumbField } from '@/features/files'

import { MAX_GENERATED_VARIANTS } from '../product-form.mapper'
import type { ProductFormValues } from '../schemas'

import { VariantPriceField } from './variant-price-field'

interface ProductVariantsFieldProps {
  control: Control<ProductFormValues>
  /**
   * The rows, and the three things that can be done to them.
   *
   * The field array itself lives in the dialog: filling the grid from the options
   * replaces every row at once, and react-hook-form only supports that through the
   * `replace` of the one `useFieldArray` that owns the list — a second instance
   * here would be writing to an array it does not control.
   */
  fields: FieldArrayWithId<ProductFormValues, 'variants', 'id'>[]
  onAdd: () => void
  onRemove: (index: number) => void
  /** Rebuilds the grid from the options. Absent while there is nothing to build from. */
  onFill?: () => void
  /** The option names every variant has to carry a value for. */
  axes: string[]
  /** Rows a full matrix would have, so the section can own up to the cap. */
  combinationCount: number
  errors: FieldErrors<ProductFormValues>
  isDisabled?: boolean
}

/**
 * The sellable rows under a product — one per combination of option values.
 *
 * Laid out as a grid rather than a stack of cards: what the merchant is doing here
 * is comparing prices down a column, and a row per variant puts three of them on
 * screen where a card put one. The column headings carry the labels, so each
 * field hides its own and the numbers drop their steppers to fit the column.
 *
 * Stock is not here: what a variant has in the drawer belongs to inventory, which
 * adjusts it through its own endpoint. A box in this form would re-assert whatever
 * it was opened with and quietly undo a count taken while the dialog was open.
 *
 * The server writes children only when there is more than one, so a single variant
 * would be dropped on save; the schema asks for a second rather than letting that
 * happen quietly. Removing a row here deletes that variant on the next save, which
 * is also how the server reads a save that leaves one out.
 */
export function ProductVariantsField({
  control,
  fields,
  onAdd,
  onRemove,
  onFill,
  axes,
  combinationCount,
  errors,
  isDisabled,
}: ProductVariantsFieldProps) {
  // `variants` carries its own message when the list as a whole is wrong — a lone
  // row — which no single field would ever show.
  const listError = errors.variants?.root?.message ?? errors.variants?.message
  const isCapped = combinationCount > MAX_GENERATED_VARIANTS

  /**
   * One track per axis, then the fixed columns.
   *
   * Written as a style rather than a class: the axis count is only known at
   * runtime, and Tailwind compiles the classes it can see in the source.
   */
  const columns = [
    '2.25rem',
    ...axes.map(() => 'minmax(6.5rem, 1fr)'),
    'minmax(8rem, 1.4fr)',
    'minmax(7rem, 1fr)',
    'minmax(6.5rem, 0.9fr)',
    '2.25rem',
  ].join(' ')

  const headings = ['Image', ...axes, 'SKU', 'Barcode', 'Price', '']

  return (
    <FormSection
      title="Variants"
      description={
        axes.length
          ? `One row per combination of ${axes.join(' × ')}.`
          : 'Add an option first — a variant has to vary along something.'
      }
      action={
        <>
          {onFill && (
            <Button
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
              onPress={onFill}
              className="border-border h-8 shrink-0 rounded-full border px-3 text-xs"
            >
              Fill from options
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            isDisabled={isDisabled || axes.length === 0}
            onPress={onAdd}
            className="border-border h-8 shrink-0 rounded-full border px-3 text-xs"
          >
            <PlusIcon width={14} height={14} />
            Add variant
          </Button>
        </>
      }
      alert={
        <>
          {listError && (
            <p role="alert" className="text-danger mt-3 text-xs">
              {listError}
            </p>
          )}
          {isCapped && (
            <p className="text-muted mt-3 text-xs">
              These options make {combinationCount} combinations. Filling the grid stops at{' '}
              {MAX_GENERATED_VARIANTS} — trim the option values, or add the rest by hand.
            </p>
          )}
        </>
      }
    >
      {fields.length === 0 ? (
        <FormSectionEmpty>
          No variants. The price above is what the product sells for.
        </FormSectionEmpty>
      ) : (
        // The grid keeps its column widths and scrolls sideways rather than
        // crushing the SKU and price cells on a narrow screen.
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <div className="min-w-[32rem]">
            <div
              className="text-muted grid items-end gap-x-3 pb-2 text-[11px] font-medium tracking-wide uppercase"
              style={{ gridTemplateColumns: columns }}
              aria-hidden="true"
            >
              {headings.map((heading, index) => (
                <span key={`${heading}-${index}`} className="truncate">
                  {heading}
                </span>
              ))}
            </div>

            <ul className="divide-border border-border divide-y border-t">
              {fields.map((field, index) => (
                <li
                  key={field.id}
                  className="grid items-start gap-x-3 py-3"
                  style={{ gridTemplateColumns: columns }}
                >
                  <Controller
                    control={control}
                    name={`variants.${index}.thumbnail`}
                    render={({ field: thumbnail }) => (
                      <ImageThumbField
                        label={`image, variant ${index + 1}`}
                        value={thumbnail.value}
                        onChange={thumbnail.onChange}
                        isDisabled={isDisabled}
                      />
                    )}
                  />

                  {axes.map((axis, axisIndex) => (
                    <TextFieldControl
                      key={axis}
                      control={control}
                      name={`variants.${index}.attributes.${axisIndex}.value`}
                      label={`${axis}, variant ${index + 1}`}
                      placeholder={axis}
                      isLabelHidden
                      isDisabled={isDisabled}
                    />
                  ))}

                  <TextFieldControl
                    control={control}
                    name={`variants.${index}.sku`}
                    label={`SKU, variant ${index + 1}`}
                    placeholder="TSHIRT-M"
                    isLabelHidden
                    isRequired
                    isDisabled={isDisabled}
                  />
                  <TextFieldControl
                    control={control}
                    name={`variants.${index}.gtin`}
                    label={`Barcode, variant ${index + 1}`}
                    placeholder="Optional"
                    isLabelHidden
                    isDisabled={isDisabled}
                  />
                  {/* Plain figures in the cell — the column heading says it is a
                      price — and the currency, steppers and error in the popover,
                      along with the variant's own cost and incentive. */}
                  <VariantPriceField control={control} index={index} isDisabled={isDisabled} />

                  <Button
                    variant="ghost"
                    isIconOnly
                    size="sm"
                    aria-label={`Remove variant ${index + 1}`}
                    isDisabled={isDisabled}
                    onPress={() => onRemove(index)}
                    className="text-muted hover:text-danger size-9 shrink-0"
                  >
                    <TrashIcon width={16} height={16} />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </FormSection>
  )
}
